import { useState } from "react";
import { supabase } from '../utils/supabaseClient';

export function useEmailChange({ currentEmail }) {
  const [showEmailFields, setShowEmailFields] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [currentPasswordForReauth, setCurrentPasswordForReauth] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailChangeRequested, setEmailChangeRequested] = useState(false);

  const handleChangeEmail = () => {
    setShowEmailFields(true);
  };

  const handleEmailFieldChange = (field, value) => {
    if (field === 'newEmail') setNewEmail(value);
    if (field === 'currentPasswordForReauth') setCurrentPasswordForReauth(value);
  };

  const handleSaveEmail = async () => {
    setEmailError('');
    if (!newEmail || !newEmail.includes('@')) {
      setEmailError('Enter a valid email address.');
      return;
    }
    if (newEmail === currentEmail) {
      setEmailError('This is already your current email.');
      return;
    }
    if (!currentPasswordForReauth) {
      setEmailError('Enter your current password to confirm this change.');
      return;
    }
    setEmailSaving(true);
    try {
      // Re-authenticate before letting the user change their email — same
      // reauth-gate usePasswordChange.js uses for password changes.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email: currentEmail,
        password: currentPasswordForReauth,
      });
      if (signInErr) {
        setEmailError('Current password is incorrect.');
        return;
      }

      const { error: updErr } = await supabase.auth.updateUser(
        { email: newEmail },
        { emailRedirectTo: `${window.location.origin}/auth/email-changed` }
      );
      if (updErr) throw updErr;

      // The email isn't actually changed yet — Supabase only applies it
      // once the confirmation link is followed. Don't touch
      // teachers.email or localStorage here; EmailChangeConfirmation.jsx
      // is what the user lands on after confirming.
      setEmailChangeRequested(true);
      setShowEmailFields(false);
      setNewEmail('');
      setCurrentPasswordForReauth('');
      setEmailError('');
    } catch (e) {
      setEmailError(e?.message || 'Could not start email change. Try again.');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleCancelEmail = () => {
    setShowEmailFields(false);
    setNewEmail('');
    setCurrentPasswordForReauth('');
    setEmailError('');
  };

  return {
    showEmailFields,
    newEmail,
    currentPasswordForReauth,
    emailError,
    emailSaving,
    emailChangeRequested,
    handleChangeEmail, handleEmailFieldChange, handleSaveEmail, handleCancelEmail,
  };
}
